/**
 * EL MENÚ: qué se ve, en qué orden, y con qué condiciones.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ESTO VIVE EN SU PROPIO ARCHIVO Y NO DENTRO DEL LAYOUT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Por un fallo que estuvo semanas escondido: la pantalla «Mis negocios» estaba
 * terminada, la ruta respondía, y **no tenía enlace en el menú**. La única
 * forma de llegar era escribir /todos en la barra de direcciones. Nadie la usó
 * nunca, y no había forma de darse cuenta — no falla nada, simplemente no
 * está.
 *
 * Con el menú aquí fuera, `rutas.test.ts` puede leerlo y comprobar que **toda
 * ruta de la aplicación se puede alcanzar**. Ese fallo ya no vuelve a pasar sin
 * que una prueba lo diga.
 *
 * Es la razón de este archivo. Si algún día se mete de nuevo dentro del layout,
 * la prueba deja de poder mirarlo.
 */

import {
  ArrowLeftRight,
  ArrowRightLeft,
  Banknote,
  Barcode,
  BarChart3,
  Bot,
  Boxes,
  Globe,
  MousePointerClick,
  MonitorSmartphone,
  MonitorPlay,
  Palette,
  ScanBarcode,
  SlidersHorizontal,
  Star,
  ChartColumn,
  CalendarClock,
  ChefHat,
  ClipboardCheck,
  ClipboardList,
  Coins,
  CookingPot,
  CreditCard,
  Factory,
  FileText,
  HandCoins,
  History,
  Landmark,
  Layers,
  ListChecks,
  LayoutDashboard,
  LayoutGrid,
  Package,
  PackageCheck,
  PackagePlus,
  Percent,
  Receipt,
  Scale,
  Scissors,
  Settings,
  ShieldCheck,
  ShoppingCart,
  MessageCircle,
  Store,
  Table,
  Tag,
  Tags,
  Ticket,
  TrendingDown,
  Truck,
  Undo2,
  Upload,
  UserRound,
  Users,
  Wallet,
  Warehouse,
  MapPin,
  Map as IconoDePlano,
  TrendingUp,
  Clock,
  UserX,
  Hourglass,
  CalendarDays,
  PackageMinus,
  PackageSearch,
  ShieldAlert,
  Target,
  Briefcase,
  Repeat,
  ScanSearch,
  Armchair,
  Stethoscope,
  BellRing,
  TicketCheck,
  Tv,
} from "lucide-react";
import { PERMISOS_DEL_CALENDARIO } from "@/features/calendario/calendario";

export type Enlace = {
  a: string;
  etiqueta: string;
  icono: typeof Package;
  permiso?: string;
  /**
   * Con CUALQUIERA de estos basta (2026-10-06): el Calendario junta varias
   * áreas y cada una se ve con su propio permiso.
   */
  permisoAlguno?: string[];
  /**
   * Hacen falta TODOS (2026-10-17): los Clientes de «Todas las sucursales»
   * son nombres y teléfonos (`pos.sell`) y lo que compró cada uno
   * (`reports.resumen`).
   */
  permisos?: string[];
  /** Aparece en la barra de pestañas del celular (solo caben 5). */
  enMovil?: boolean;
  /** El nombre en esa barra, si el de siempre no cabe en una línea («Consultor»). */
  etiquetaCorta?: string;
  /**
   * Otras palabras con que la gente llama a esta pantalla, para el buscador
   * del menú (`BuscadorDelMenu`): quien busca «tasa» no sabe que se llama
   * Monedas. Sin tildes ni mayúsculas que cuidar: el buscador las quita.
   */
  claves?: string;
  /**
   * Solo aparece si el negocio tiene ese interruptor encendido.
   *
   * Es lo que hace que Softfi sea simple para una barbería de una persona y
   * completo para una bodega: lo que no usas no ocupa sitio en el menú.
   */
  soloSiAjuste?: keyof import("@/lib/types").TenantSettings;
  /**
   * Con `soloSiAjuste`: si el negocio nunca tocó ese interruptor, cuenta como
   * encendido en este modo (2026-10-21). Producción nace encendida en un
   * restaurante y apagada en una bodega, sin migrar los ajustes de nadie.
   */
  encendidoEnModo?: "general" | "restaurante" | "ropa" | "consultorio";


  /**
   * Lo que vive DENTRO de esta sección.
   *
   * ═══════════════════════════════════════════════════════════════════════
   *  ⚠️ PARA QUIEN YA SE SABE EL SISTEMA
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Lo pidió el dueño con estas palabras: «así alguien que domina el sistema
   * solo da un clic a donde tiene que ir y no 2».
   *
   * Y es exacto. Categorías, impuestos, importar o los códigos de descuento
   * viven dentro de Catálogo, así que llegar a ellos era entrar a Catálogo y
   * buscar el enlace. Para quien entra dos veces al día está bien; para quien
   * lleva el negocio todos los días es un clic de más, todos los días.
   *
   * Se despliegan solos cuando se está dentro de la sección, y se pueden abrir
   * a mano desde fuera.
   */
  hijos?: Enlace[];
  /**
   * La función de Premium que hace falta para usar esta pantalla.
   *
   * ⚠️ El enlace **NO se esconde** cuando falta: se le pone un candado y sigue
   * ahí. Esconderlo dejaría al dueño sin saber que existe, y nadie paga por lo
   * que no sabe que hay. La pantalla de destino enseña el muro con lo que se
   * está perdiendo.
   */
  premium?: import("@/components/Premium").FuncionPremium;
  /**
   * Como `premium`, pero **escondiendo el enlace** en vez de poner un candado.
   *
   * Para lo que todavía NO se puede usar ni pagando. La diferencia con
   * `premium` no es técnica, es de honestidad: un candado promete algo que se
   * compra, y prometer lo que aún no funciona quema la confianza que hace falta
   * para vender lo que sí.
   */
  soloSiFuncion?: import("@/components/Premium").FuncionPremium;
  /**
   * Solo aparece si el negocio está en ese modo de operación.
   *
   * No vale `soloSiAjuste` para esto: aquel comprueba que un interruptor esté
   * encendido, y el modo es un texto («restaurante»), no un sí/no. Con
   * `soloSiAjuste` cualquier modo distinto de vacío daría verdadero, y una
   * bodega acabaría con «Salón» en el menú.
   */
  soloSiModo?: "general" | "restaurante" | "ropa" | "consultorio";
  /** Lo contrario: se esconde si el negocio está en ese modo. */
  noEnModo?: "general" | "restaurante" | "ropa" | "consultorio";
  /** Solo si el negocio tiene más de un local: con uno no hay qué comparar. */
  soloSiVariasSucursales?: boolean;
  /**
   * Se ilumina solo en su dirección exacta, sin contar las que cuelgan de ella.
   *
   * ⚠️ Solo para la barra del almacén (`ALMACEN`): ahí «Inventario» y
   * «Entradas» van uno al lado del otro, mientras que en la barra de siempre
   * «Entradas» cuelga de «Inventario». `esExacto` mira la barra de siempre, y
   * meter la del almacén en su cuenta cambiaría cómo se ilumina aquella.
   */
  exacto?: boolean;
  /**
   * La subopción que la pantalla enseña cuando la dirección no dice cuál
   * (sin `?ver=`): se ilumina también entonces. Para las pestañas de «Todas las
   * sucursales», donde entrar a «Ventas» a secas es entrar a «Cada sucursal».
   */
  porDefecto?: boolean;
};

export type Grupo = {
  titulo: string;
  enlaces: Enlace[];
};

/** La acción principal. Va aparte de los grupos, con su propio tratamiento. */
export const VENDER: Enlace = {
  a: "/vender", claves: "cobrar caja punto de venta pos facturar",
  etiqueta: "Vender",
  icono: ShoppingCart,
  permiso: "pos.sell",
  enMovil: true,
};

/**
 * En un restaurante, el botón grande es SALÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO PUEDE DECIR «VENDER» EN UN RESTAURANTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque ahí no se vende de una: se sienta gente, se apunta, se manda a la
 * cocina, y el dinero entra al final. El mesero que pulsa «Vender» esperando
 * atender una mesa cae en la pantalla de cobrar de una bodega — que existe y
 * funciona, pero no es lo que vino a hacer, y encima le deja cobrar por fuera
 * del salón sin que la mesa se entere.
 *
 * El botón grande es el gesto que se repite cincuenta veces por turno. En una
 * bodega ese gesto es cobrar; en un restaurante es abrir el salón.
 *
 * ⚠️ La entrada «Salón» del menú se queda igual: un enlace repetido en la barra
 * no estorba, y quitarla dejaría al salón sin sitio fijo el día que alguien
 * cambie este botón.
 */
export const SALON: Enlace = {
  a: "/salon",
  etiqueta: "Salón",
  icono: LayoutGrid,
  permiso: "pos.sell",
  enMovil: true,
};

/**
 * INICIO — fuera de todo grupo, y a propósito.
 *
 * Estaba dentro de «Operación», y el dueño lo señaló: «Inicio lo vas a sacar,
 * eso no va en operación». Tiene razón y el motivo es de fondo: Inicio no es
 * una tarea del negocio, es el resumen de TODAS. Meterlo en un grupo lo pone al
 * mismo nivel que «Gastos» o «Clientes», cuando su sitio es por encima —junto a
 * «Vender», que es la otra cosa que se hace sin pensar dónde está.
 */
export const INICIO: Enlace = {
  a: "/", claves: "resumen del dia panel tablero",
  etiqueta: "Inicio",
  icono: LayoutDashboard,
  enMovil: true,
};

// =============================================================================
//  LOS GRUPOS
// =============================================================================
//
// ═══════════════════════════════════════════════════════════════════════════
//  ⚠️ «OPERACIÓN» YA NO EXISTE, Y ERA CATORCE ENLACES
// ═══════════════════════════════════════════════════════════════════════════
//
// El dueño lo dijo así: «siento que estamos metiendo todo en operación y ya,
// más bien deberían haber otras secciones».
//
// Y era literal. Dentro de «Operación» convivían vender, cobrar, mirar
// reportes, cuadrar la caja, apuntar gastos, ver clientes y saber lo que se le
// debe al proveedor. Un grupo que contiene todo no agrupa nada: el título deja
// de ayudar a buscar y la barra se lee como una lista de catorce cosas sueltas.
//
// Peor todavía con el título plegable: plegar «Operación» escondía la mitad de
// la aplicación de golpe, así que nadie la plegaba nunca y la barra era siempre
// igual de larga.
//
// Ahora cada grupo contesta una pregunta distinta, y esa es la regla para
// decidir dónde va algo nuevo:
//
//   · **Mostrador** — lo que se hace con el cliente delante.
//   · **Dinero**    — lo que entra, lo que sale y lo que queda debiendo.
//   · **Análisis**  — lo que se mira para decidir, no para trabajar.
//   · **Negocio**   — lo que se configura una vez y se retoca de vez en cuando.
//   · **Administrar** — quién entra y cómo está puesto todo.
//
// ⚠️ Si una pantalla nueva parece caber en dos, cabe en la que responde la
// pregunta que se hace ANTES de abrirla. «Por cobrar» toca clientes, pero nadie
// entra ahí pensando en un cliente: entra pensando en la plata que le falta.

export const GRUPOS: Grupo[] = [
  {
    /*
      MOSTRADOR — lo que pasa con el cliente delante.

      Es el grupo de quien está de pie trabajando: vender, devolver, atender.
      Va primero porque es lo que más se toca y porque, en un negocio con
      empleados, muchas veces es lo ÚNICO que ellos pueden abrir.
    */
    titulo: "Mostrador",
    enlaces: [
      /*
        El consultorio (2026-10-30): agenda, sala de espera y pacientes van
        primero, porque es lo que la recepción y el médico abren todo el día.
        Entra quien agenda o quien atiende; el expediente lo cierra el
        servidor a quien no atiende.
      */
      {
        a: "/agenda",
        claves: "citas agendar turno cita consulta calendario doctor medico",
        etiqueta: "Agenda",
        icono: CalendarDays,
        permisoAlguno: ["consultorio.agenda", "consultorio.atender"],
        soloSiModo: "consultorio",
        enMovil: true,
      },
      {
        a: "/sala-de-espera",
        claves: "espera llegada pacientes hoy turno atender cobrar consulta",
        etiqueta: "Sala de espera",
        etiquetaCorta: "Sala",
        icono: Armchair,
        permisoAlguno: ["consultorio.agenda", "consultorio.atender"],
        soloSiModo: "consultorio",
        enMovil: true,
      },
      {
        a: "/pacientes",
        claves: "historia clinica expediente pacientes ficha consultas recetas",
        etiqueta: "Pacientes",
        icono: Stethoscope,
        permisoAlguno: ["consultorio.agenda", "consultorio.atender"],
        soloSiModo: "consultorio",
      },
      /*
        ⚠️ EL REGISTRO COMPLETO CUELGA DE VENTAS, Y ANTES SOLO SE LLEGABA POR UN
        BOTÓN DENTRO DE LA PANTALLA.

        Son dos preguntas distintas y las dos son de todos los días:

          · «¿Qué he vendido hoy?»  → la lista de hoy, que se mira de pie.
          · «¿Qué vendí esta semana, con qué me pagaron y por dónde entró?»
            → el registro, con su rango de fechas y sus columnas.

        Con el registro escondido detrás de un botón, quien quería cuadrar la
        semana tenía que entrar a Ventas primero y encontrar el botón. Un clic
        de más, todos los días, para la pregunta que más se hace al cerrar.

        Mismo criterio que Catálogo o Inventario: lo que vive dentro de una
        sección se alcanza desde el menú, no solo desde dentro.
      */
      {
        a: "/ventas", claves: "vendido historial",
        etiqueta: "Ventas",
        icono: Receipt,
        permiso: "pos.sell",
        enMovil: true,
        hijos: [
          {
            a: "/ventas/registro", claves: "historial facturas tickets anular",
            // Se llama como la pantalla a la que lleva. «Ver todas» no decía a
            // dónde iba ni qué se iba a encontrar allí.
            etiqueta: "Registro de ventas",
            icono: Table,
            permiso: "reports.registro_ventas",
          },
        ],
      },
      /*
        ⚠️ COTIZACIONES VA **DEBAJO** DE VENTAS, NO DENTRO.

        Lo tuve colgando de Ventas y el dueño lo corrigió: «dije debajo de
        ventas, no en ventas».

        Y es lo correcto. Dentro solo cuelga lo que es *la misma cosa mirada de
        otra forma*: el registro de ventas son las mismas ventas con más
        columnas. Una cotización no es una venta — no cobró nada, no movió
        inventario, y se abre para otra tarea: llamar a quien todavía no ha
        dicho que sí.

        Colgada de Ventas además se esconde: el submenú está plegado casi
        siempre, así que quien no supiera que existe no la encontraría nunca.

        Con `pos.sell` y no `reports.view`: cotizar es atender a alguien que
        pregunta cuánto le sale, y eso lo hace quien está en el mostrador. No
        hay dinero de por medio que proteger.
      */
      {
        a: "/cotizaciones", claves: "presupuesto proforma",
        etiqueta: "Cotizaciones",
        icono: FileText,
        permiso: "pos.cotizaciones",
        noEnModo: "restaurante",
      },
      /*
        ⚠️ LA MISMA PANTALLA, CON OTRO NOMBRE EN RESTAURANTE (2026-10-14).

        En un restaurante no se cotiza: ahí solo llegan los pedidos de la página
        web (y, más adelante, los del autoservicio) para facturarlos. Lo pidió
        el dueño: «en este módulo no se puede hacer cotizaciones, entonces solo
        servirían para pedidos que vengan de la página web».
      */
      {
        a: "/cotizaciones", claves: "presupuesto proforma",
        etiqueta: "Pedidos recibidos",
        icono: FileText,
        permiso: "pos.cotizaciones",
        soloSiModo: "restaurante",
      },
      /*
        Apartados: en el MOSTRADOR, junto a Ventas y Cotizaciones (2026-10-26).
        Estaba en Dinero, al lado de «Por cobrar», y el dueño lo movió: «está en
        dinero, nada que ver». Un apartado se hace, se abona y se entrega con el
        cliente delante, como una venta. Con su propio interruptor: hay tiendas
        de ropa que apartan a todo el mundo y no le fían a nadie.
      */
      {
        a: "/apartados", claves: "reservas separados",
        etiqueta: "Apartados",
        icono: PackageCheck,
        permiso: "pos.sell",
        soloSiAjuste: "apartado_enabled",
        // Un restaurante no aparta platos (lo señaló el dueño, 2026-10-15): en
        // ese modo el interruptor tampoco sale en Funciones.
        noEnModo: "restaurante",
      },
      // ── El salón, solo en modo restaurante ───────────────────────────────
      //
      // Va con `enMovil`: en un restaurante es LA pantalla, la que el mesero
      // abre cincuenta veces por turno desde el celular.
      //
      // `soloSiModo` la hace desaparecer del todo en los demás negocios: una
      // bodega con un enlace a «Salón» solo se pregunta qué hizo mal.
      /*
        ⚠️ «SALÓN» YA NO VA AQUÍ. El dueño: «si pusimos el botón grande de
        salón, hay que quitar el pequeño».

        Tiene razón: en modo restaurante el botón grande de arriba ya dice
        Salón y lleva al mismo sitio. Repetirlo en la lista de abajo hace dudar
        de si son dos pantallas distintas — y en el celular gastaba una de las
        cinco pestañas de la barra, donde el sitio es de verdad escaso.

        La entrada existe igual, en `SALON`, arriba de este archivo.
      */
      // La cocina va JUNTO al salón y no escondida en un submenú: en muchos
      // sitios es la única pantalla que abre esa persona en todo el turno, y
      // desde una tablet colgada de la pared no hay quien navegue.
      {
        a: "/cocina",
        etiqueta: "Cocina",
        icono: ChefHat,
        permiso: "pos.sell",
        enMovil: true,
        soloSiModo: "restaurante",
      },
      // Reservaciones (2026-10-27): junto al salón y a la cocina, que es donde
      // trabaja quien recibe al cliente. Con su interruptor en Funciones,
      // encendido de fábrica en un restaurante.
      {
        a: "/reservaciones",
        claves: "reservar reserva mesa apartar cita booking anfitrion",
        etiqueta: "Reservaciones",
        icono: CalendarClock,
        permiso: "resto.reservaciones",
        soloSiModo: "restaurante",
        soloSiAjuste: "reservaciones_enabled",
        encendidoEnModo: "restaurante",
      },
      // Devoluciones: solo dueño y supervisor. Devolver es sacar dinero del
      // cajón sin que entre nada a cambio, así que el enlace ni siquiera
      // aparece para un cajero — y el backend lo vuelve a comprobar.
      {
        a: "/devoluciones", claves: "reembolso cambio",
        etiqueta: "Devoluciones y cambios",
        icono: Undo2,
        permiso: "pos.refund",
        noEnModo: "restaurante",
      },
      // En restaurante no hay cambio por otro producto (2026-10-15): un plato se
      // rehace o se reembolsa. La misma pantalla, sin el paso del cambio.
      {
        a: "/devoluciones", claves: "reembolso cambio",
        etiqueta: "Devoluciones",
        icono: Undo2,
        permiso: "pos.refund",
        soloSiModo: "restaurante",
      },
      /*
        Clientes: quiénes son y cómo ubicarlos.

        ⚠️ Va en MOSTRADOR y no en Negocio (junto al catálogo), porque no es
        algo que se configura una vez: es a quien se le vende todos los días, y
        se entra a buscarlo cuando alguien llama preguntando por su pedido.
      */
      { a: "/clientes", claves: "compradores contactos", etiqueta: "Clientes", icono: UserRound, permiso: "pos.sell" },
      /*
        El consultor de precios: la pantalla con el lector que se deja en el
        local para que el cliente mire cuánto cuesta algo sin preguntar.

        ⚠️ Abre una pantalla completa, fuera de Softfi, de la que se sale con
        una clave. Ver `features/consultor/`.
      */
      // En restaurante no hay lector ni estantes que consultar: se esconde.
      {
        a: "/consultar", claves: "precio verificador",
        etiqueta: "Consultor de precios",
        icono: ScanBarcode,
        permiso: "catalog.view",
        noEnModo: "restaurante",
      },
      /*
        El autoservicio (2026-10-15): la pantalla de la entrada donde el
        cliente del restaurante arma su pedido solo. Ocupa el lugar del
        consultor de precios, que en restaurante no se usa.

        ⚠️ Como el consultor, la pantalla del cliente va FUERA del menú
        (`/autoservicio/pantalla`) y se sale con clave. Aquí está el panel
        para diseñarla y el botón para activarla en el equipo.
      */
      {
        a: "/autoservicio",
        etiqueta: "Autoservicio",
        icono: MonitorSmartphone,
        permiso: "catalog.view",
        soloSiModo: "restaurante",
        premium: "autoservicio",
      },
      /*
        Turnos (2026-11-02): el dispensador donde el cliente toma su número, el
        televisor que lo llama y la pantalla de quien atiende. Para el modo
        general —supermercados, ferreterías, farmacias— y con su interruptor
        en Funciones. «Atender turnos» cuelga de aquí: es lo que abre el
        cajero todo el día, a un clic.
      */
      {
        a: "/turnos",
        claves: "turno ticket fila cola numero dispensador llamar siguiente supermercado television",
        etiqueta: "Turnos",
        icono: TicketCheck,
        permisoAlguno: ["pos.sell", "turnos.atender"],
        soloSiModo: "general",
        soloSiAjuste: "turnos_enabled",
        premium: "turnos",
        hijos: [
          {
            a: "/turnos/atender",
            claves: "llamar siguiente turno puesto caja mostrador",
            etiqueta: "Atender turnos",
            icono: BellRing,
            permisoAlguno: ["pos.sell", "turnos.atender"],
            soloSiModo: "general",
            soloSiAjuste: "turnos_enabled",
            premium: "turnos",
          },
        ],
      },
      /*
        La pantalla publicitaria (2026-11-02): los anuncios del negocio en un
        televisor del local. En TODOS los modos: la sala de espera de un
        consultorio, la entrada de un restaurante, una tienda de ropa.
      */
      {
        a: "/publicidad",
        claves: "anuncios flyers propaganda publicidad promociones television pantalla tv carteles",
        etiqueta: "Pantalla publicitaria",
        icono: Tv,
        permiso: "catalog.view",
        soloSiAjuste: "publicidad_enabled",
        premium: "publicidad",
      },
    ],
  },
  /*
    ⚠️ «NEGOCIO» VA ANTES QUE «DINERO», y el orden lo pidió el dueño.

    Tiene sentido por cómo se usa Softfi de verdad: primero se monta el
    negocio —el catálogo, el inventario— y solo entonces empieza a haber
    dinero que mirar. Con «Dinero» arriba, quien acaba de entrar lo primero
    que ve son cuentas y cobros de un negocio que aún no tiene productos.
  */
  {
    /*
      NEGOCIO — lo que se configura una vez y se retoca de vez en cuando.

      Qué vendes, qué tienes y a cuánto está el dólar. Se entra aquí a
      PREPARAR, no a despachar: por eso va después de lo del día a día aunque
      sin catálogo no se pueda vender nada.
    */
    titulo: "Negocio",
    enlaces: [
      {
        a: "/catalogo", claves: "productos articulos mercancia",
        etiqueta: "Catálogo",
        icono: Package,
        permiso: "catalog.view",
        hijos: [
          /*
            ⚠️ PRODUCTOS Y SERVICIOS, SEPARADOS, ANTES QUE LO DEMÁS.

            «Catálogo» a secas no dice qué hay dentro. Un dueño de barbería
            entra buscando sus servicios y se encuentra una lista con todo
            mezclado; uno de bodega busca productos y ve cortes de cabello.

            Son la misma pantalla con el filtro puesto, y aun así importa: un
            enlace que dice «Servicios» promete servicios, y llegar a ellos con
            el filtro ya aplicado es la diferencia entre encontrarlos y no saber
            que se pueden separar.
          */
          { a: "/catalogo?tipo=product", etiqueta: "Productos", icono: Package, permiso: "catalog.view" },
          { a: "/catalogo?tipo=service", etiqueta: "Servicios", icono: Scissors, permiso: "catalog.view" },
          {
            a: "/catalogo?tipo=combo",
            etiqueta: "Recetas y combos",
            icono: Layers,
            permiso: "catalog.view",
          },
          { a: "/catalogo/categorias", claves: "departamentos familias", etiqueta: "Categorías", icono: Tags, permiso: "catalog.manage" },
          {
            a: "/catalogo/precios", claves: "cambiar precios aumentar",
            // Se llama como la pantalla, no como la técnica. «En lote» describe
            // CÓMO se editan; lo que la gente busca es QUÉ se edita.
            etiqueta: "Precios de venta",
            icono: Tag,
            permiso: "catalog.precios",
          },
          { a: "/catalogo/codigos", claves: "cupones promociones descuentos", etiqueta: "Códigos de descuento", icono: Ticket, permiso: "catalog.codigos" },
          {
            /*
              ⚠️ SU PROPIA ENTRADA, Y SOLO EN RESTAURANTE.

              Antes los grupos solo se podían tocar desde dentro del formulario
              de un plato: para crear uno había que abrir una receta cualquiera
              que no tenía nada que ver. Un grupo es del NEGOCIO —«Extras» se
              pone en las cinco hamburguesas—, así que va donde están las otras
              cosas del negocio: al lado de Categorías e Impuestos.

              `soloSiModo` porque en una bodega no existe la pregunta: nadie
              pide un jabón «de pollo o de carne».
            */
            a: "/catalogo/opciones",
            etiqueta: "Extras",
            icono: ListChecks,
            permiso: "catalog.opciones",
            soloSiModo: "restaurante",
          },
          { a: "/catalogo/importar", claves: "excel cargar productos subir lista", etiqueta: "Importar", icono: Upload, permiso: "catalog.importar" },
        ],
        enMovil: true,
      },
      {
        a: "/inventario", claves: "stock existencias mercancia",
        etiqueta: "Inventario",
        icono: Boxes,
        permiso: "inventory.view",
        hijos: [
          { a: "/inventario/entradas", claves: "compras facturas de proveedor recibir mercancia", etiqueta: "Entradas", icono: PackagePlus, permiso: "inventory.entradas" },
          /*
            Cargar la existencia de una sucursal desde una lista. Lo trajo un
            cliente con dos sucursales: el catálogo ya estaba cargado con la
            primera, y el importador de Catálogo marca todo «ya lo tienes».
          */
          { a: "/inventario/cargar-existencias", claves: "ajuste conteo stock inicial", etiqueta: "Cargar existencias", icono: Upload, permiso: "inventory.ajustes" },
          { a: "/inventario/movimientos", claves: "kardex historial de stock", etiqueta: "Movimientos", icono: ArrowLeftRight, permiso: "inventory.view" },
          { a: "/inventario/traslados", claves: "enviar mercancia otra sucursal guia de despacho", etiqueta: "Traslados", icono: Truck, permiso: "inventory.view" },
          // Donde se guarda la mercancía para despacharla (2026-10-01): Premium y gratis.
          {
            a: "/inventario/almacenes",
            etiqueta: "Almacenes",
            icono: Warehouse,
            permiso: "tenant.sucursales",
            premium: "multi_sucursal",
          },
          { a: "/inventario/proveedores", claves: "distribuidores", etiqueta: "Proveedores", icono: Factory, permiso: "inventory.view" },
        ],
      },
      /*
        Producción por tandas (2026-10-21): pan, galletas, tortas, salsas —lo
        que se elabora ANTES de venderse—. Justo debajo de Inventario porque es
        de donde le llega la existencia a lo que se hornea.

        Solo con el interruptor de Funciones; sin nada guardado, encendido en
        restaurante (`encendidoEnModo`). Una bodega no tiene qué producir.
      */
      {
        a: "/produccion",
        claves: "fabricacion horno hornear elaborar panaderia pasteleria cocina tandas lotes formulas recetas preparar galletas",
        etiqueta: "Producción",
        icono: CookingPot,
        permiso: "inventory.produccion",
        soloSiAjuste: "produccion_enabled",
        encendidoEnModo: "restaurante",
        hijos: [
          { a: "/produccion?ver=formulas", etiqueta: "Fórmulas", icono: Layers, permiso: "inventory.produccion", porDefecto: true },
          { a: "/produccion?ver=registro", claves: "historial producciones", etiqueta: "Registro", icono: ClipboardList, permiso: "inventory.produccion" },
        ],
      },
      /*
        Control de merma (2026-10-27): contar los ingredientes y ver la
        desviación contra lo que dicen las recetas. Va con el inventario de
        cocina, debajo de Producción. Por ahora solo en el restaurante: fuera de
        él la palabra «merma» y las recetas no dicen lo mismo.
      */
      {
        a: "/merma",
        claves: "conteo contar inventario fisico toma desviacion faltante sobrante ingredientes perdida",
        etiqueta: "Control de merma",
        icono: ClipboardCheck,
        permiso: "inventory.conteo",
        soloSiModo: "restaurante",
      },
      /*
        Las etiquetas con el precio y el código de barras (2026-10-21).

        Van en NEGOCIO, junto al catálogo y al inventario, porque se imprimen
        al preparar la mercancía —cuando llega o cuando cambian los precios—,
        no con el cliente delante. Con su propia entrada y no dentro de
        Catálogo: el que etiqueta suele ser quien recibe la mercancía, y lo
        busca por su nombre.

        `noEnModo: "restaurante"`: un plato no lleva etiqueta en ningún
        estante. Lo pidió así el dueño al encargarla.
      */
      {
        a: "/etiquetas",
        claves: "etiquetadora imprimir codigo de barras precios stickers adhesivos rotular habladores",
        etiqueta: "Etiquetas",
        icono: Barcode,
        permiso: "catalog.etiquetas",
        noEnModo: "restaurante",
      },
      /*
        El catálogo web va en NEGOCIO, junto al catálogo y el inventario: es la
        misma mercancía, enseñada hacia fuera. Con candado sin Premium, no
        escondido: nadie paga por lo que no sabe que existe.
      */
      {
        a: "/catalogo-web", claves: "tienda en linea pagina web pedidos whatsapp",
        etiqueta: "Catálogo web",
        icono: Globe,
        permiso: "catalog.web",
        premium: "catalogo_web",
        // Las pestañas de la pantalla, a un clic desde el menú, igual que
        // Catálogo e Inventario. La pantalla lee la pestaña de la dirección.
        hijos: [
          { a: "/catalogo-web?pestana=diseno", etiqueta: "Diseño", icono: Palette, permiso: "catalog.web" },
          { a: "/catalogo-web?pestana=productos", etiqueta: "Productos", icono: Package, permiso: "catalog.web" },
          { a: "/catalogo-web?pestana=tendencias", etiqueta: "Tendencias", icono: Star, permiso: "catalog.web" },
          { a: "/catalogo-web?pestana=trafico", etiqueta: "Tráfico", icono: MousePointerClick, permiso: "catalog.web" },
          { a: "/catalogo-web?pestana=ajustes", etiqueta: "Ajustes", icono: SlidersHorizontal, permiso: "catalog.web" },
        ],
      },
    ],
  },
  {
    /*
      DINERO — lo que entra, lo que sale y lo que queda debiendo.

      Aquí vive todo lo que mueve plata de verdad. Están juntos a propósito:
      cuadrar el día es mirar la caja, los gastos y lo que quedó fiado, y
      tenerlos repartidos por la barra obligaba a acordarse de los tres.
    */
    titulo: "Dinero",
    enlaces: [
      /*
        ⚠️ CUENTAS VA ANTES QUE CAJA, Y NO ES UN CAPRICHO DE ORDEN.

        Cuentas contesta «¿cuánto tengo en total y dónde?»; Caja contesta
        «¿cuánto hay en el cajón ahora mismo?». La primera es la pregunta
        grande y la segunda una de sus partes — la cuenta de efectivo que sale
        en Cuentas ES el cajón, leído del mismo arqueo.

        Puestas al revés, quien entra buscando su dinero encuentra primero el
        cajón y cree que eso es todo lo que Softfi sabe.
      */
      {
        a: "/cuentas", claves: "bancos dinero saldo",
        etiqueta: "Cuentas",
        icono: Landmark,
        permiso: "finanzas.cuentas",
      },
      /*
        ⚠️ MÉTODOS DE PAGO: FUERA DE AJUSTES Y FUERA DE CUENTAS.

        Primero estaba enterrado en Ajustes, entre el logo y las sucursales,
        como algo que se configura una vez y se olvida. No lo es: son las formas
        en que entra el dinero.

        Después lo colgué DENTRO de Cuentas y también estaba mal — el dueño lo
        corrigió en el acto: «no quiero que esté dentro de cuentas, quiero que
        sea independiente». Y es cierto: los métodos de pago existían antes que
        las cuentas y siguen valiendo sin ellas. Un negocio puede cobrar por
        Zelle sin haber creado una sola cuenta.

        Meterlo dentro dice «esto es una parte de aquello», y no lo es: son dos
        cosas que se relacionan, no una dentro de otra.

        ⚠️ La ruta NO cambia: sigue siendo `/ajustes/metodos-pago`. Mover algo de
        sitio en el menú no tiene por qué romper los enlaces que ya circulan.
      */
      {
        a: "/ajustes/metodos-pago", claves: "pago movil zelle binance punto efectivo formas de pago",
        etiqueta: "Métodos de pago",
        icono: CreditCard,
        permiso: "tenant.metodos_pago",
      },
      /*
        ⚠️ LAS LISTAS DE PRECIOS SALIERON DE AJUSTES Y VINIERON AQUÍ.

        Estaban enterradas en Ajustes, entre cosas que se tocan una vez y no se
        vuelven a mirar. Pero una lista de precios NO es una configuración: es
        algo que se revisa y se retoca cada vez que suben los costos, igual que
        los métodos de pago que tiene justo encima.

        Y desde que el precio de mayor por cantidad se retiró, esto es LO ÚNICO
        que hay para cobrarle distinto a alguien. Dejarlo escondido en Ajustes
        era esconder la función entera.
      */
      {
        a: "/ajustes/listas-de-precios", claves: "mayorista detal precios especiales",
        etiqueta: "Listas de precios",
        icono: Tags,
        permiso: "catalog.precios",
      },
      /*
        ⚠️ IMPUESTOS SALIÓ DE CATÁLOGO Y VINO AQUÍ.

        Lo pidió el dueño de Softfi: los impuestos no dicen qué se vende sino
        cuánto se cobra, y se configuran una vez — como los métodos de pago y
        las listas de precios, que tiene justo encima. Aquí vive también lo de
        marcar muchos productos exentos de una vez: la harina sin IVA.

        ⚠️ Con su propia dirección, `/impuestos`. Colgando de `/catalogo/…`,
        tocarlo desplegaba Catálogo en la barra. La vieja sigue funcionando y
        manda aquí.
      */
      {
        a: "/impuestos", claves: "iva igtf",
        etiqueta: "Impuestos",
        icono: Percent,
        permiso: "catalog.manage",
      },
      {
        a: "/caja", claves: "abrir caja cerrar caja cierre arqueo efectivo",
        etiqueta: "Caja",
        icono: Wallet,
        permiso: "cash.open",
        enMovil: true,
        hijos: [
          /*
            ⚠️ El historial dejó de ser una ventanita dentro de Caja.

            Era un botón «Turnos anteriores» que abría un modal con una lista de
            fechas: valía para «¿cuándo cerré la última vez?» y para nada más.
            No se podía buscar en él, ni enlazarlo, ni dejarlo abierto al lado
            de otra cosa.

            Ahora es una pantalla con su tabla —quién cerró, cuánto duró, con
            qué le pagaron, si cuadró— y por eso tiene su sitio en la barra: lo
            que vive dentro de una sección se alcanza desde el menú, no solo
            desde dentro.
          */
          {
            a: "/caja/historial",
            etiqueta: "Historial de caja",
            icono: History,
            permiso: "cash.open",
          },
          /*
            ⚠️ Las cajas FÍSICAS, que hasta ahora no se podían crear.

            Cada sucursal nacía con «Caja 1» y ahí se acababa. El dueño lo tumbó
            con su caso: «en un súper no solo hay una caja». Con dos personas
            cobrando en el mismo local y un solo turno, el faltante del cierre es
            de las dos juntas y no se puede señalar de dónde salió — que es lo
            único que el arqueo viene a resolver.

            Pide `tenant.manage` y no `cash.open`: abrir un turno lo hace el
            cajero; decidir cuántas cajas tiene el negocio, no.
          */
          {
            a: "/caja/cajas",
            etiqueta: "Cajas",
            icono: Wallet,
            permiso: "tenant.cajas",
          },
        ],
      },
      /*
        ⚠️ GASTOS TIENE SU PROPIA ENTRADA, Y ANTES ESTABA ENTERRADO EN CAJA.

        Un auditor pidió «pestaña de gastos» sin saber que ya se podían
        registrar — y tenía razón en pedirla: el gasto se anotaba dentro de un
        turno de caja y al cerrarlo dejaba de verse. Nadie va a buscar «en qué
        se me fue la plata este mes» dentro del arqueo del martes.

        Va justo debajo de Caja porque de ahí sale el dinero, pero como entrada
        propia porque la pregunta es otra.
      */
      { a: "/gastos", claves: "egresos salidas pagos", etiqueta: "Gastos", icono: TrendingDown, permiso: "cash.movement" },
      /*
        ⚠️ NÓMINA VA EN DINERO, NO EN «EL NEGOCIO» CON EL EQUIPO.

        Es la tentación, porque habla de personas. Pero la pregunta que se hace
        quien la abre no es «quién trabaja aquí» —eso es Equipo— sino **«a quién
        le tengo que pagar y con qué»**, que es exactamente la misma pregunta de
        Gastos y de Por pagar. Por eso está entre las dos.

        Y con su propio permiso: lo que cobra cada quien es de lo más delicado
        que hay en un negocio pequeño. Un cajero que descubre que su compañero
        gana más que él es un problema el mismo día.
      */
      { a: "/nomina", claves: "empleados sueldos salarios pagos al personal", etiqueta: "Nómina", icono: Banknote, permiso: "nomina.manage" },
      {
        a: "/credito", claves: "deudas cobrar clientes que deben credito",
        // El nombre que usó el auditor y el que ya llevaba la pantalla por
        // dentro. «Crédito» a secas no le decía a nadie que ahí está lo que le
        // deben — y hace pareja con «Por pagar», que es su espejo.
        etiqueta: "Por cobrar",
        icono: HandCoins,
        permiso: "pos.sell",
      },
      // ── Y su espejo: lo que el negocio DEBE ─────────────────────────────
      //
      // Va justo debajo del fiado porque son la misma idea en las dos
      // direcciones: lo que te deben y lo que debes.
      //
      // ⚠️ Pero con OTRO permiso. El cajero necesita ver el fiado —tiene al
      // cliente delante y decide si le sigue fiando— y no tiene por qué saber
      // que su jefe le debe cuatro mil dólares al proveedor. Eso es de la
      // misma familia que el costo de un producto.
      //
      // Y sin `soloSiAjuste`: deber no es una función que se enciende. Todo
      // negocio le debe a alguien, y esconderlo tras un interruptor solo
      // consigue que nadie lo encuentre.
      {
        a: "/por-pagar", claves: "deudas proveedores cuentas por pagar",
        etiqueta: "Por pagar",
        icono: Receipt,
        permiso: "finanzas.por_pagar",
      },
      // ── Pagos por revisar: ESCONDIDO hasta que la pasarela funcione ──────
      //
      // `soloSiFuncion` lo hace desaparecer del menú mientras la verificación
      // contra los bancos siga apagada (`PASARELA_HABILITADA` en el backend).
      //
      // No lleva candado y no se anuncia, a diferencia de Equipo o del análisis
      // de reportes, y la razón es sencilla: **aquello existe y esto todavía
      // no**. Un candado sobre algo que se puede comprar es una oferta; sobre
      // algo que no se puede usar ni pagando, es una molestia — el dueño entra,
      // lee "próximamente", y aprende que en Softfi hay botones que no llevan a
      // ningún sitio.
      //
      // Vuelve solo, sin tocar nada aquí, el día que se encienda la pasarela y
      // el negocio tenga Premium. Cuando eso pase, va en Dinero: es trabajo del
      // día, no configuración, y una bandeja escondida tras dos clics no la
      // mira nadie.
      {
        a: "/pagos-por-revisar",
        etiqueta: "Pagos por revisar",
        icono: ShieldCheck,
        permiso: "reports.view",
        soloSiFuncion: "pasarela",
      },
      /*
        ⚠️ MONEDAS VIVE EN «DINERO», Y ANTES ESTABA EN «NEGOCIO».

        Lo dijo el dueño en una línea: «creo que monedas va en dinero». Y
        tiene razón — en «Negocio» están las cosas que se configuran una vez
        (el catálogo, los datos de la tienda). La tasa del dólar se mira y se
        cambia TODOS LOS DÍAS, igual que la caja y los métodos de pago.

        Buscarla entre los ajustes es buscarla donde no se usa.
      */
      {
        a: "/monedas", claves: "tasa dolar bcv bolivares cambio divisas",
        etiqueta: "Monedas",
        icono: Coins,
        permiso: "currency.view",
        hijos: [
          /*
            ⚠️ La calculadora se cuelga aquí y no es una página aparte.

            Es un cuadro que se abre encima de Monedas, y con razón: convertir
            se hace MIRANDO las tasas. Lo que cambia es que ahora se llega de un
            toque desde la barra, que es lo que pidió el dueño — entrar en
            Monedas y después buscar el botón son dos toques para una sola
            intención, y en Venezuela esto se usa veinte veces al día.
          */
          {
            a: "/monedas?calculadora=1",
            etiqueta: "Calculadora",
            icono: ArrowRightLeft,
            permiso: "currency.view",
          },
        ],
      },
    ],
  },
  {
    /*
      ANÁLISIS — lo que se mira para decidir, no para trabajar.

      La diferencia con «Dinero» es el tiempo verbal. En Dinero se registra lo
      que está pasando; aquí se mira lo que ya pasó para decidir lo que viene.
      Nadie abre Reportes con un cliente esperando.

      Por eso también es el grupo que un empleado casi nunca ve: los dos
      enlaces piden permisos que no lleva su cargo.
    */
    titulo: "Análisis",
    enlaces: [
      /*
        ⚠️ REPORTES SE PLIEGA, Y SUS SECCIONES ESTABAN ESCONDIDAS.

        Cuentas e Historial de costos solo se alcanzaban bajando hasta el final
        de la pantalla de Reportes. Una sección a la que únicamente se llega
        desde dentro de otra es una sección que la mitad de la gente no
        encuentra nunca — y estas dos contestan las preguntas de cierre de mes.

        Las mismas cuatro salen aquí y como pestañas dentro de la pantalla, a
        propósito: unos las buscan en la barra y otros dentro, y ninguna de las
        dos costumbres está mal.
      */
      {
        a: "/reportes", claves: "ganancia utilidad resumen financiero",
        /*
          ⚠️ SE LLAMA «RESUMEN FINANCIERO» Y ANTES «REPORTES».

          Dentro cuelga ahora una pantalla que también se llama «Reportes» —la
          del detalle del período—, y «Reportes › Reportes» no se puede leer.
          Es el mismo choque que frenó llamar «Cuentas» a la sección de
          clientes y deudas: dos sitios con el mismo nombre en la misma barra
          hacen que quien busca entre en el que no era y concluya que el dato
          no existe.

          «Resumen financiero» es además la palabra que usó el dueño.
        */
        etiqueta: "Resumen financiero",
        icono: ChartColumn,
        permiso: "reports.view",
        /*
          ⚠️ ESTE ORDEN TIENE QUE SER EL MISMO QUE EL DE LAS PESTAÑAS.

          Las mismas cinco secciones se alcanzan por dos caminos: esta lista y
          la barra de pestañas de dentro (`features/reportes/SeccionesDeReportes`).
          Estaban en orden distinto, y eso confunde más de lo que parece: uno
          aprende dónde está «Cuentas» mirando la barra lateral, entra, y en las
          pestañas está en otro sitio. La memoria de dónde queda cada cosa deja
          de servir y hay que leer las etiquetas cada vez.

          Manda el orden de las pestañas porque es el que tiene un motivo: va de
          la pregunta más general a la más concreta — cómo fue el período
          (Resumen) → qué pasó exactamente (Reportes) → cuánto gané y dónde
          quedó (Utilidad) → en qué cuenta está (Cuentas).

          «Resumen» no está aquí porque es esta misma entrada: `/reportes`.
        */
        hijos: [
          {
            a: "/reportes/detalle", claves: "excel descargar informes",
            etiqueta: "Reportes",
            icono: Table,
            permiso: "reports.detalle",
          },
          /*
            ⚠️ «Utilidad y pérdida» es el nombre que usó el dueño, y se respeta
            aunque «Rentabilidad» suene más corto. Es como se llama en la calle:
            quien busca si ganó este mes escribe «utilidad», no «rentabilidad».
          */
          {
            a: "/reportes/utilidad",
            etiqueta: "Utilidad y pérdida",
            icono: Scale,
            permiso: "reports.utilidad",
          },
          { a: "/reportes/cuentas", etiqueta: "Cuentas", icono: Wallet, permiso: "reports.cuentas" },
          {
            a: "/reportes/estadisticas", claves: "ranking mas vendidos graficas",
            etiqueta: "Estadísticas",
            icono: BarChart3,
            permiso: "reports.estadisticas",
          },
          /*
            Rotación y predicción (2026-10-04): el ANÁLISIS de los productos —
            cuánto tardan en venderse, qué hacer con cada uno, cuánto comprar—.
            Van después de Estadísticas porque son el paso siguiente: de «cómo
            va» a «qué hago».
          */
          {
            a: "/reportes/rotacion",
            etiqueta: "Rotación de productos",
            icono: Repeat,
            permiso: "reports.rotacion",
            premium: "analisis",
          },
          {
            a: "/reportes/prediccion",
            etiqueta: "Predicción de demanda",
            icono: TrendingUp,
            permiso: "reports.prediccion",
            premium: "analisis",
          },
          {
            a: "/reportes/costos",
            etiqueta: "Historial de costos",
            icono: History,
            // El historial de costos ES el margen. Sin este permiso no aparece
            // ni apagado: enseñarlo solo informa de que hay datos vedados.
            permiso: "reports.historial_costos",
          },
          // Cada producto con su precio en cada lista (2026-10-12).
          { a: "/reportes/precios", etiqueta: "Precios", icono: Tags, permiso: "reports.precios" },
        ],
      },
      // ── Sofi ─────────────────────────────────────────────────────────────
      //
      // Junto a Reportes porque contesta lo mismo por otro camino: se le
      // pregunta en palabras lo que en Reportes hay que ir a buscar.
      //
      // Lleva `premium` y NO `soloSiFuncion`: a diferencia de la verificación
      // de pagos, esto SÍ funciona y sí se puede comprar. El candado es una
      // oferta, no una promesa.
      { a: "/sofi", claves: "asistente inteligencia artificial preguntar ayuda", etiqueta: "Sofi", icono: Bot, permiso: "pos.sell", premium: "analisis" },
    ],
  },
  {
    titulo: "Administrar",
    enlaces: [
      /*
        El Calendario (2026-10-06): lo que pasó y lo que viene en todo el
        negocio. Aquí y no en una sola área porque las junta todas; lo pidió el
        dueño así. Con el permiso de cualquiera de ellas se entra.
      */
      {
        a: "/calendario", claves: "agenda fechas vencimientos",
        etiqueta: "Calendario",
        icono: CalendarDays,
        permisoAlguno: PERMISOS_DEL_CALENDARIO,
      },
      { a: "/equipo", claves: "empleados usuarios permisos cajeros vendedores", etiqueta: "Equipo", icono: Users, permiso: "users.view", premium: "equipo" },
      /*
        Sucursales y el bot salieron de Ajustes por el mismo motivo que las
        listas de precios: no son cosas que se configuren una vez. Un segundo
        local se mira, y el bot se enciende, se apaga y se revisa. Aquí están
        junto a Equipo, que es la otra pantalla de «quién y qué hay en el
        negocio».
      */
      {
        a: "/ajustes/sucursales", claves: "tiendas locales sedes",
        etiqueta: "Sucursales",
        icono: Store,
        permiso: "tenant.sucursales",
      },
      {
        a: "/ajustes/bot", claves: "whatsapp avisos notificaciones",
        etiqueta: "Softfi Bot",
        icono: MessageCircle,
        permiso: "tenant.manage",
      },
      { a: "/ajustes", claves: "configuracion negocio logo", etiqueta: "Ajustes", icono: Settings, permiso: "tenant.manage" },
      /*
        Tutoriales (2026-10-26): los videos de Softfi, debajo de Ajustes porque
        lo pidió el dueño así. Sin permiso: aprender a usar lo que uno tiene
        delante es para todo el equipo.
      */
      { a: "/tutoriales", claves: "videos ayuda aprender como se usa guia curso", etiqueta: "Tutoriales", icono: MonitorPlay },
    ],
  },
];

// =============================================================================
//  «TODOS»: EL MENÚ DE QUIEN MIRA VARIOS NEGOCIOS A LA VEZ
// =============================================================================
//
// ═══════════════════════════════════════════════════════════════════════════
//  ⚠️ POR QUÉ ES UN MENÚ ENTERO Y NO UNA PANTALLA MÁS
// ═══════════════════════════════════════════════════════════════════════════
//
// «Todos» era un enlace que llevaba a una pantalla, con la barra lateral de la
// Barbería El Corte todavía a la izquierda. Y eso no se sostiene: al lado del
// resumen de los cuatro negocios seguía habiendo un «Caja» y un «Inventario»
// que eran de uno solo. El dueño mira las cifras de todo y pulsa «Catálogo»
// esperando el catálogo de todo — y aterriza en el de la barbería.
//
// Elegir «Todos» es un cambio de sitio, igual que cambiar de negocio. Por eso
// cambia la barra entera: lo que hay dentro vuelve a ser todo de lo mismo.
//
// ═══════════════════════════════════════════════════════════════════════════
//  LO QUE ENTRA Y LO QUE NO
// ═══════════════════════════════════════════════════════════════════════════
//
// Entra lo que responde una pregunta **de varios sitios a la vez**: cuánto
// vendieron todos, qué local se quedó atrás, quién trabaja dónde.
//
// No entra nada de operar. Vender, cobrar, abrir caja o cargar mercancía pasan
// siempre en un sitio concreto, y un botón de «Vender» aquí solo puede llevar
// a preguntar «¿vender dónde?». Para eso se entra al negocio.

// `GRUPO` se arma DESPUÉS de `TODAS_LAS_SUCURSALES`: es la misma barra (2026-10-03).

// ═══════════════════════════════════════════════════════════════════════════
//  «TODAS LAS SUCURSALES»: SU PROPIA BARRA (2026-09-29)
// ═══════════════════════════════════════════════════════════════════════════
//
// La hermana de `GRUPO`, un piso más abajo: aquella mira todos los negocios,
// esta todas las sucursales de UN negocio. Se entra desde «Sucursales → Todas»
// del selector, y es un cambio de sitio como aquel: dentro de un negocio, las
// pantallas enseñan solo la sucursal elegida (ver `reports/alcance.py`); aquí,
// todas juntas y una al lado de otra.
//
// Tampoco entra nada de operar: vender, abrir caja o cargar mercancía pasan en
// una sucursal concreta, y para eso se entra en ella (cada tarjeta tiene su
// «Entrar»).
//
// Desde el 2026-10-02, seis pestañas en tres grupos (lo pidió el dueño: «una
// base bien pobre… añade más pestañas»): las ventas, el dinero —con lo que se
// cobra y se paga en todas— y la mercancía.
export const TODAS_LAS_SUCURSALES: Grupo[] = [
  /*
    ⚠️ CADA PESTAÑA, CON EL PERMISO DE SU PANTALLA DE DENTRO (2026-10-17).

    Entrar aquí es `reports.sucursales` (el selector y las rutas lo piden);
    cada enlace pide además lo mismo que su pantalla dentro de una sucursal:
    las ventas, «Resumen financiero»; la mercancía, el inventario; el dinero,
    los costos. Con la entrada sola, el empleado de Cashea —que solo iba a
    mirar existencias— veía las ventas y los vendedores de todas.
  */
  /*
    ⚠️ CADA PESTAÑA, UNA SUBOPCIÓN (2026-10-02).

    Lo pidió el dueño con la barra a la vista: «tiene que ser muy completo y
    detallado», con subopciones plegables como las de Inventario. Cada pantalla
    ya tenía sus pestañas por dentro (`?ver=`, ver `useOpcion`), escondidas
    detrás de un clic; ahora están en la barra y se llega de uno. La primera
    de cada una es la que se ve sin elegir (`porDefecto`).
  */
  {
    titulo: "Todas las sucursales",
    enlaces: [
      {
        a: "/sucursales",
        etiqueta: "Resumen",
        icono: LayoutDashboard,
        permiso: "reports.resumen",
        enMovil: true,
        exacto: true,
      },
      /*
        El calendario de todas (2026-10-07), justo después del resumen: lo que
        pasó y lo que viene en cada sucursal, y lo atrasado de toda la empresa.
      */
      {
        a: "/sucursales/calendario",
        etiqueta: "Calendario",
        icono: CalendarDays,
        permiso: "reports.sucursales",
      },
      {
        a: "/sucursales/ventas",
        etiqueta: "Ventas",
        icono: ChartColumn,
        permiso: "reports.resumen",
        enMovil: true,
        hijos: [
          { a: "/sucursales/ventas?ver=comparar", etiqueta: "Por sucursal", icono: Store, porDefecto: true },
          { a: "/sucursales/ventas?ver=productos", etiqueta: "Productos", icono: Package },
          // La ganancia es de quien ve los costos (sin ellos la pestaña no sale).
          {
            a: "/sucursales/ventas?ver=rentabilidad",
            etiqueta: "Rentabilidad",
            icono: TrendingUp,
            permiso: "reports.view_costs",
          },
          { a: "/sucursales/ventas?ver=categorias", etiqueta: "Por categoría", icono: Tags },
          { a: "/sucursales/ventas?ver=pagos", etiqueta: "Métodos de pago", icono: CreditCard },
          { a: "/sucursales/ventas?ver=horarios", etiqueta: "Horarios de venta", icono: Clock },
          { a: "/sucursales/ventas?ver=equipo", etiqueta: "Por vendedor", icono: UserRound },
          // La cuarta tanda (2026-10-02).
          // Lo anulado, descontado y devuelto: el mismo permiso que el Registro.
          {
            a: "/sucursales/ventas?ver=control",
            etiqueta: "Control de ventas",
            icono: ShieldAlert,
            permiso: "reports.registro_ventas",
          },
          {
            a: "/sucursales/ventas?ver=cotizaciones",
            etiqueta: "Cotizaciones",
            icono: FileText,
            permiso: "pos.cotizaciones",
          },
          { a: "/sucursales/ventas?ver=metas", etiqueta: "Metas de venta", icono: Target },
        ],
      },
      // Quién compra, quién vuelve y quién dejó de venir (2026-10-02). Nombres
      // y teléfonos de clientes: el mismo permiso que la lista de clientes; y
      // lo que compró cada uno, el de las ventas (2026-10-17).
      {
        a: "/sucursales/clientes",
        etiqueta: "Clientes",
        icono: Users,
        permisos: ["pos.sell", "reports.resumen"],
        hijos: [
          { a: "/sucursales/clientes?ver=mejores", etiqueta: "Principales clientes", icono: Star, porDefecto: true },
          { a: "/sucursales/clientes?ver=varias", etiqueta: "Clientes multisucursal", icono: Layers },
          { a: "/sucursales/clientes?ver=perdidos", etiqueta: "Clientes inactivos", icono: UserX },
          { a: "/sucursales/clientes?ver=sucursales", etiqueta: "Por sucursal", icono: Store },
        ],
      },
      // Sofi mirando todas las sucursales a la vez (2026-10-03, lo pidió el
      // dueño: «en mis sucursales mete a Sofi»). Ver `sofi/sucursales.py`.
      {
        a: "/sucursales/sofi",
        etiqueta: "Sofi",
        icono: Bot,
        permiso: "reports.resumen",
        premium: "analisis",
      },
    ],
  },
  {
    titulo: "Dinero",
    enlaces: [
      // El dinero de todas es cosa de quien ve los costos: lo que ganó cada
      // una, lo que tiene en el banco y lo que debe.
      {
        a: "/sucursales/dinero",
        etiqueta: "Dinero",
        icono: Wallet,
        permiso: "reports.view_costs",
        enMovil: true,
        hijos: [
          { a: "/sucursales/dinero?ver=resultados", etiqueta: "Estado de resultados", icono: Scale, porDefecto: true },
          { a: "/sucursales/dinero?ver=cuentas", etiqueta: "Saldos por cuenta", icono: Landmark },
          { a: "/sucursales/dinero?ver=gastos", etiqueta: "Gastos", icono: TrendingDown },
          // Los cierres de todos: el mismo permiso que el historial de cajas.
          { a: "/sucursales/dinero?ver=cajas", etiqueta: "Cierres de caja", icono: Banknote, permiso: "cash.view_all" },
          // Lo que cobra cada quien: el mismo permiso que Nómina (2026-10-02).
          { a: "/sucursales/dinero?ver=nomina", etiqueta: "Nómina", icono: Briefcase, permiso: "nomina.manage" },
        ],
      },
      // Los mismos permisos que Por cobrar y Por pagar dentro de una sucursal.
      {
        a: "/sucursales/por-cobrar",
        etiqueta: "Por cobrar",
        icono: HandCoins,
        permiso: "pos.sell",
        hijos: [
          { a: "/sucursales/por-cobrar?ver=clientes", etiqueta: "Por cliente", icono: Users, porDefecto: true },
          { a: "/sucursales/por-cobrar?ver=antiguedad", etiqueta: "Antigüedad de saldos", icono: Hourglass },
          { a: "/sucursales/por-cobrar?ver=sucursales", etiqueta: "Por sucursal", icono: Store },
        ],
      },
      {
        a: "/sucursales/por-pagar",
        etiqueta: "Por pagar",
        icono: Receipt,
        permiso: "finanzas.por_pagar",
        hijos: [
          { a: "/sucursales/por-pagar?ver=cuentas", etiqueta: "Detalle de cuentas", icono: Receipt, porDefecto: true },
          { a: "/sucursales/por-pagar?ver=calendario", etiqueta: "Calendario de pagos", icono: CalendarDays },
          { a: "/sucursales/por-pagar?ver=proveedores", etiqueta: "Por proveedor", icono: Factory },
        ],
      },
    ],
  },
  {
    titulo: "Mercancía",
    enlaces: [
      {
        a: "/sucursales/inventario",
        etiqueta: "Inventario",
        icono: Boxes,
        permiso: "inventory.view",
        enMovil: true,
        hijos: [
          { a: "/sucursales/inventario?ver=resumen", etiqueta: "Resumen", icono: LayoutDashboard, porDefecto: true },
          { a: "/sucursales/inventario?ver=existencias", etiqueta: "Existencias", icono: Boxes },
          { a: "/sucursales/inventario?ver=parados", etiqueta: "Sin rotación", icono: Hourglass },
          { a: "/sucursales/inventario?ver=categorias", etiqueta: "Por categoría", icono: Tags },
          { a: "/sucursales/inventario?ver=mandar", etiqueta: "Reposición sugerida", icono: ArrowRightLeft },
          { a: "/sucursales/inventario?ver=traslados", etiqueta: "Traslados", icono: Truck },
          // La tercera tanda (2026-10-02).
          { a: "/sucursales/inventario?ver=mermas", etiqueta: "Mermas y ajustes", icono: PackageMinus },
          {
            a: "/sucursales/inventario?ver=compras",
            etiqueta: "Compras",
            icono: PackagePlus,
            permiso: "reports.view_costs",
          },
          { a: "/sucursales/inventario?ver=precios", etiqueta: "Diferencias de precio", icono: Tag, permiso: "catalog.view" },
          { a: "/sucursales/inventario?ver=faltan", etiqueta: "Productos faltantes", icono: PackageSearch },
        ],
      },
      /*
        Un producto en todas las sucursales y almacenes, buscado o escaneado
        (2026-10-07). Lo pidió el dueño: «un consultor de productos en mis
        sucursales… con todos sus datos». No es el Consultor de precios: aquel
        es la pantalla del cliente; este, una pantalla normal para el negocio.
      */
      {
        a: "/sucursales/consultor",
        etiqueta: "Consultor de productos",
        etiquetaCorta: "Consultor",
        icono: ScanSearch,
        permiso: "inventory.view",
        enMovil: true,
      },
    ],
  },
  /*
    El análisis de los productos (2026-10-05): las dos secciones del Resumen
    financiero, con cada sucursal comparada y el consolidado de todas. Lo pidió
    el dueño: «hacerlo ahora en mis sucursales también».
  */
  {
    titulo: "Análisis",
    enlaces: [
      {
        a: "/sucursales/rotacion",
        etiqueta: "Rotación de productos",
        icono: Repeat,
        permiso: "reports.rotacion",
        premium: "analisis",
      },
      {
        a: "/sucursales/prediccion",
        etiqueta: "Predicción de demanda",
        icono: TrendingUp,
        permiso: "reports.prediccion",
        premium: "analisis",
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  «TODOS MIS NEGOCIOS»: LA MISMA BARRA, UN PISO ARRIBA (2026-10-03)
// ═══════════════════════════════════════════════════════════════════════════
//
// Lo pidió el dueño con las dos barras delante: «mira todos mis negocios vs
// todas mis sucursales, faltan plegables, faltan un montón de cosas… lo que
// hicimos con todas mis sucursales también en todos mis negocios». Las
// pantallas son las mismas (ver `features/sucursales/ambito.tsx`), así que la
// barra también: se arma desde `TODAS_LAS_SUCURSALES`, cambiando la dirección
// y lo que dice «sucursal». Lo que se agregue allí aparece aquí solo.
//
// Fuera quedan las dos opciones que solo existen dentro de UN negocio: mandar
// mercancía de una sucursal a otra y copiar productos entre sus catálogos.
//
// Entra lo que responde una pregunta de varios sitios a la vez; nada de
// operar. Vender, cobrar o cargar mercancía pasan en un negocio concreto, y
// para eso se entra en él («Ingresar»).

const A_NEGOCIOS: Record<string, string> = {
  "Por sucursal": "Por negocio",
  "Clientes multisucursal": "Clientes en varios negocios",
};

const SOLO_ENTRE_SUCURSALES = new Set([
  "/sucursales/inventario?ver=mandar",
  "/sucursales/inventario?ver=faltan",
  // Rotación y predicción (2026-10-05): todavía no existen entre negocios.
  "/sucursales/rotacion",
  "/sucursales/prediccion",
  // El consultor de productos (2026-10-07): las copias del mismo producto se
  // reconocen por su vínculo, que solo existe dentro de un negocio.
  "/sucursales/consultor",
]);

function deNegocios(e: Enlace): Enlace {
  return {
    ...e,
    a: e.a.replace(/^\/sucursales/, "/grupo"),
    etiqueta: A_NEGOCIOS[e.etiqueta] ?? e.etiqueta,
    hijos: e.hijos?.filter((h) => !SOLO_ENTRE_SUCURSALES.has(h.a)).map(deNegocios),
  };
}

export const GRUPO: Grupo[] = [
  {
    titulo: "Todos mis negocios",
    enlaces: [
      { a: "/grupo", etiqueta: "Resumen", icono: Layers, enMovil: true, exacto: true },
      // Se llama «Negocios» y no «Sucursales» porque eso es lo que hay dentro:
      // cada negocio con sus locales debajo, y lo que hay que revisar en él.
      { a: "/grupo/negocios", etiqueta: "Negocios", icono: Store, enMovil: true },
      // Ventas, Clientes y Sofi: los de «Todas las sucursales», menos su Resumen.
      ...(TODAS_LAS_SUCURSALES[0]?.enlaces
        .slice(1)
        .filter((e) => !SOLO_ENTRE_SUCURSALES.has(e.a))
        .map(deNegocios) ?? []),
    ],
  },
  ...TODAS_LAS_SUCURSALES.slice(1)
    .map((g) => ({ ...g, enlaces: g.enlaces.filter((e) => !SOLO_ENTRE_SUCURSALES.has(e.a)).map(deNegocios) }))
    // Un grupo que se quedó sin enlaces no se dibuja: sería un título suelto.
    .filter((g) => g.enlaces.length > 0),
];

// ═══════════════════════════════════════════════════════════════════════════
//  ⚠️ DENTRO DE UN ALMACÉN: SU PROPIA BARRA (2026-10-01)
// ═══════════════════════════════════════════════════════════════════════════
//
// Lo pidió el dueño al ver la barra completa estando en un almacén: «quita
// más cosas cuando se está dentro y deja las necesarias, como la pantalla de
// todas mis sucursales». Un almacén guarda y despacha: no vende, no cobra, no
// tiene clientes ni catálogo web. Su barra es solo lo de la mercancía, y se
// sale de él con el selector, como de «Todas».
//
// «Por pagar» se queda: lo que se le debe al proveedor por una entrada a
// crédito queda en el almacén que la recibió.
export const ALMACEN: Grupo[] = [
  {
    titulo: "Almacén",
    enlaces: [
      {
        a: "/inventario", claves: "stock existencias mercancia",
        etiqueta: "Inventario",
        icono: Boxes,
        permiso: "inventory.view",
        enMovil: true,
        exacto: true,
      },
      {
        a: "/inventario/entradas", claves: "compras facturas de proveedor recibir mercancia",
        etiqueta: "Entradas",
        icono: PackagePlus,
        permiso: "inventory.entradas",
        enMovil: true,
      },
      // Pasillos, estantes y niveles: dónde está cada cosa (2026-10-07).
      {
        a: "/inventario/ubicaciones",
        etiqueta: "Ubicaciones",
        icono: MapPin,
        permiso: "inventory.view",
      },
      // El galpón dibujado, con cada estante del color de lo lleno que está
      // (2026-10-11). En la barra y no solo dentro de Ubicaciones: el dueño lo
      // buscó aquí y no lo encontró.
      {
        a: "/inventario/plano",
        etiqueta: "Plano",
        icono: IconoDePlano,
        permiso: "inventory.view",
      },
      {
        a: "/inventario/traslados", claves: "enviar mercancia otra sucursal guia de despacho",
        etiqueta: "Traslados",
        icono: Truck,
        permiso: "inventory.view",
        enMovil: true,
      },
      {
        a: "/inventario/movimientos", claves: "kardex historial de stock",
        etiqueta: "Movimientos",
        icono: ArrowLeftRight,
        permiso: "inventory.view",
        enMovil: true,
      },
      {
        a: "/inventario/cargar-existencias", claves: "ajuste conteo stock inicial",
        etiqueta: "Cargar existencias",
        icono: Upload,
        permiso: "inventory.ajustes",
      },
    ],
  },
  {
    titulo: "Mercancía",
    enlaces: [
      { a: "/catalogo", claves: "productos articulos mercancia", etiqueta: "Productos", icono: Package, permiso: "catalog.view", enMovil: true },
      { a: "/inventario/proveedores", claves: "distribuidores", etiqueta: "Proveedores", icono: Factory, permiso: "inventory.view" },
      { a: "/por-pagar", claves: "deudas proveedores cuentas por pagar", etiqueta: "Por pagar", icono: Receipt, permiso: "finanzas.por_pagar" },
    ],
  },
  {
    titulo: "Administrar",
    enlaces: [
      {
        a: "/inventario/almacenes",
        etiqueta: "Almacenes",
        icono: Warehouse,
        permiso: "tenant.sucursales",
        premium: "multi_sucursal",
      },
      // También dentro de un almacén: sus pantallas tienen su ayuda.
      { a: "/tutoriales", claves: "videos ayuda aprender como se usa guia curso", etiqueta: "Tutoriales", icono: MonitorPlay },
    ],
  },
];

/**
 * ¿Este enlace debe marcarse solo con la dirección EXACTA?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL FALLO: DOS OPCIONES ILUMINADAS A LA VEZ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * React Router marca un enlace como activo cuando la dirección **empieza** por
 * la suya. Así que estando en `/grupo/negocios` se encendían dos: «Negocios»,
 * que es donde estás, y «Resumen», porque `/grupo` es el principio de
 * `/grupo/negocios`.
 *
 * Y eso rompe lo único que el menú tiene que hacer bien: decir dónde estás. Con
 * dos encendidos deja de decirlo, y encima parece un fallo de dibujo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE CALCULA Y NO SE MARCA A MANO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Poner una casilla `exacto: true` en `/grupo` arreglaría hoy y se pudriría
 * mañana: el día que alguien cuelgue `/reportes/costos` del menú, «Reportes»
 * empieza a iluminarse doble y nadie lo relaciona con aquella casilla.
 *
 * Aquí se pregunta a la lista entera: un enlace es exacto **si otro enlace del
 * menú cuelga de él**. Añadir un enlace nuevo lo arregla solo.
 */
export function esExacto(direccion: string): boolean {
  if (direccion === "/") return true;
  return enlacesDelMenu().some((e) => e.a !== direccion && e.a.startsWith(`${direccion}/`));
}

/**
 * ¿La página en la que se está tiene su propio enlace en el menú, colgado de
 * `direccion`? En Softfi Bot (`/ajustes/bot`) la respuesta para «Ajustes» es
 * sí: esa página es de Softfi Bot, y Ajustes no debe encenderse con ella.
 *
 * Es la otra mitad de `esExacto`: aquella apaga el relleno de la fila, y esta
 * apaga el icono en color y la negrita, que se calculan aparte.
 */
export function esDeOtroEnlace(direccion: string, ruta: string): boolean {
  return enlacesDelMenu().some(
    (e) =>
      e.a !== direccion &&
      e.a.startsWith(`${direccion}/`) &&
      (ruta === e.a || ruta.startsWith(`${e.a}/`)),
  );
}

function enlacesDelMenu(): Enlace[] {
  return [
    VENDER,
    ...GRUPOS.flatMap((g) => g.enlaces),
    ...GRUPO.flatMap((g) => g.enlaces),
    ...TODAS_LAS_SUCURSALES.flatMap((g) => g.enlaces),
  ];
}
